import { Link } from "react-router-dom";
import { useState, useContext, useEffect } from "react";
import {
  faHouse,
  faPenToSquare,
  faCalendarDays,
  faUsers,
  faPlus,
  faBullhorn,
  faChartBar,
  faFolderOpen,
  faShareNodes,
  faHandshake,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Brand from "../shared/Brand";
import { useTranslation } from "react-i18next";
import NewModal from "../shared/Modals/NewModal";
import GroupForm from "@serie3/common/Modules/Groups/Forms/GroupForm";
import { useFormik } from "formik";
import SaveButton from "../shared/Buttons/SaveButton";
import Group from "@serie3/common/Modules/Groups/Entities/Group";
import { Col, Row } from "react-bootstrap";
import TextField from "../Form/TextField";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { useGroupLogo } from "../shared/GroupLogo";

const GROUP_COLORS = [
  "#6aabce", "#6dbf99", "#d4906e", "#8c82c4",
  "#c9aa52", "#5db8cc", "#c47a9e", "#c98f50",
];

const groupColor = (id) => GROUP_COLORS[id % GROUP_COLORS.length];

// Uploaded group logo when there is one, otherwise a coloured initial.
const GroupAvatar = ({ group }) => {
  const logoUrl = useGroupLogo({ groupId: group.id });
  const hasLogo = Boolean(logoUrl);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 26,
        borderRadius: 7,
        overflow: "hidden",
        background: hasLogo ? "#fff" : groupColor(group.id),
        color: "#fff",
        fontSize: 11,
        fontWeight: 700,
        marginRight: 10,
        flexShrink: 0,
        letterSpacing: 0,
        textTransform: "uppercase",
      }}
    >
      {hasLogo ? (
        <img src={logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
      ) : (
        group.name?.charAt(0) ?? "G"
      )}
    </span>
  );
};

const targetRoles = ["ROLE_ADMIN", "ROLE_SUPER_ADMIN", "ROLE_OG_USER"];

const Sidebar = ({ showSidebar = false, groups = [], setGroups }) => {
  const [isContentMenuOpen, setIsContentMenuOpen] = useState(true);
  const [isGrowthMenuOpen, setIsGrowthMenuOpen] = useState(false);
  const [isAccountabilityMenuOpen, setIsAccountabilityMenuOpen] = useState(false);
  const [isGroupMenuOpen, setIsGroupMenuOpen] = useState(false);
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [showExperimentalNav, setShowExperimentalNav] = useState(false);
  const [editData] = useState();
  // Posts planned on my account that still wait for my decision.
  const [pendingApprovals, setPendingApprovals] = useState(0);

  const { settings } = useContext(SettingsContext);

  const { t } = useTranslation();

  const groupForm = GroupForm.createGroupForm(groups, setGroups);
  const formik = useFormik(groupForm);

  useEffect(() => {
    if (!settings?.user?.roles) return; // ⛔ skip until roles are loaded

    const roles = Object.values(settings.user.roles);
    const hasTargetRole = roles.some((role) => targetRoles.includes(role));

    setShowExperimentalNav(hasTargetRole);
  }, [settings]);

  useEffect(() => {
    if (!settings?.user) return;
    let cancelled = false;
    crudApi
      .justGet(SocialEndpoints.posts.schedule.approvals.inbox)
      .then((r) => {
        if (cancelled || !r.ok) return;
        const list = r.data?.schedules ?? [];
        setPendingApprovals(list.filter((s) => s.approval_status === "proposed").length);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [settings]);


  const Save = () => {
    return (
      <>
        <SaveButton
          submitFunction={(e) => {
            formik.handleSubmit(e);
            e.preventDefault();
          }}
        />
      </>
    );
  };

  return (
    <>
      <NewModal
        show={showNewGroupForm}
        setShow={setShowNewGroupForm}
        title={formik.values.title}
        ExtraButton={Save}
        fullscreen={false}
        onShow={() => {
          editData
            ? formik.setValues(GroupForm.setEditData(editData))
            : formik.setValues(Group);
        }}
        onExit={() => {
          formik.resetForm({ values: { Group } });
        }}
      >
        <Row>
          <Col>
            <TextField
              formik={formik}
              label={t("Backend.Post.Title")}
              valueName={"name"}
              style={{ marginBottom: "10px" }}
            />
          </Col>
        </Row>
      </NewModal>
      <div
        className="vertical-menu"
        style={{
          display: showSidebar ? "block" : "",
          position: "absolute",
          overflow: "hidden scroll",
          scrollbarColor: "#fff #e9ebef",
          scrollbarWidth: "thin",
        }}
      >
        <div
          className="navbar-brand-box"
          style={{ height: "70px", paddingTop: "20px" }}
        >
          <Brand />
        </div>
        {groups && (
          <div
            data-simplebar="init"
            className="sidebar-menu-sidebar-menu-scroll simplebar-scrollable-y"
          >
            <div className="simplebar-wrapper">
              <div className="simplebar-height-auto-observer-wrapper">
                <div className="simplebar-height-auto-observer"></div>
              </div>
              <div className="simplebar-mask" style={{ marginTop: "70px" }}>
                <div
                  className="simplebar-offset"
                  style={{ right: "0px", bottom: "0px" }}
                >
                  <div
                    className="simplebar-content-wrapper"
                    tabIndex="0"
                    role="region"
                    aria-label="scrollable content"
                    style={{ overflow: "hidden scroll;" }}
                  >
                    <div
                      className="simplebar-content"
                      style={{ padding: "0px" }}
                    >
                      <div id="sidebar-menu">
                        <ul className="metismenu list-unstyled">

                          {/* ── Navigation ───────────────────────── */}
                          <li>
                            <Link to="/">
                              <span className="nav-icon"><FontAwesomeIcon icon={faHouse} /></span>
                              <span className="menu-item">Dashboard</span>
                            </Link>
                          </li>

                          {/* ── Approvals: posts planned on my account ── */}
                          <li>
                            <Link to="/approvals">
                              <span className="nav-icon"><FontAwesomeIcon icon={faCircleCheck} /></span>
                              <span className="menu-item">Approvals</span>
                              {pendingApprovals > 0 && (
                                <span style={{
                                  marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                                  background: "#e0a800", color: "#1a2236", verticalAlign: "middle",
                                }}>
                                  {pendingApprovals}
                                </span>
                              )}
                            </Link>
                          </li>

                          {/* ── Content ──────────────────────────── */}
                          <li>
                            <Link
                              to="#"
                              onClick={(e) => { e.preventDefault(); setIsContentMenuOpen(!isContentMenuOpen); }}
                              className="has-arrow waves-effect"
                            >
                              <span className="nav-icon"><FontAwesomeIcon icon={faPenToSquare} /></span>
                              <span className="menu-item">Content</span>
                            </Link>
                            <ul className={`sub-menu mm-${isContentMenuOpen ? "show" : "collapse"}`}>
                              <li><Link to="/pillars/"><span className="menu-item">Pillars</span></Link></li>
                              <li><Link to="/timeline"><span className="menu-item">Posts</span></Link></li>
                              <li><Link to="/recycler"><span className="menu-item">Recycling</span></Link></li>
                              <li><Link to="/calendar"><span className="menu-item">Calendar</span></Link></li>
                            </ul>
                          </li>

                          {/* ── Assets ───────────────────────────── */}
                          <li>
                            <Link to="/files">
                              <span className="nav-icon"><FontAwesomeIcon icon={faFolderOpen} /></span>
                              <span className="menu-item">Files</span>
                            </Link>
                          </li>

                          {/* ── Collab ───────────────────────────── */}
                          <li>
                            <Link
                              to="#"
                              onClick={(e) => { e.preventDefault(); setIsGroupMenuOpen(!isGroupMenuOpen); }}
                              className="has-arrow waves-effect"
                            >
                              <span className="nav-icon"><FontAwesomeIcon icon={faHandshake} /></span>
                              <span className="menu-item">Groups</span>
                            </Link>
                            <ul className={`sub-menu mm-${isGroupMenuOpen ? "show" : "collapse"}`}>
                              <li>
                                <Link
                                  to="#"
                                  onClick={(e) => { e.preventDefault(); setShowNewGroupForm(true); }}
                                  className="waves-effect"
                                >
                                  <span className="nav-icon"><FontAwesomeIcon icon={faPlus} /></span>
                                  <span className="menu-item">New</span>
                                </Link>
                              </li>
                              {groups.map((group) => (
                                <li key={group.id}>
                                  <Link to={`/groups/${group.id}`} className="waves-effect">
                                    <GroupAvatar group={group} />
                                    <span className="menu-item" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140, display: "inline-block", verticalAlign: "middle" }}>{group.name}</span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </li>

                          {/* ── Growth (admin only) ───────────────── */}
                          {showExperimentalNav && (
                            <>
                              <li>
                                <Link
                                  to="#"
                                  onClick={(e) => { e.preventDefault(); setIsGrowthMenuOpen(!isGrowthMenuOpen); }}
                                  className="has-arrow waves-effect"
                                >
                                  <span className="nav-icon"><FontAwesomeIcon icon={faBullhorn} /></span>
                                  <span className="menu-item">Outreach</span>
                                </Link>
                                <ul className={`sub-menu mm-${isGrowthMenuOpen ? "show" : "collapse"}`}>
                                  <li><Link to="/outreach/messages"><span className="menu-item">Messages</span></Link></li>
                                  <li><Link to="/outreach/messagelog"><span className="menu-item">Message log</span></Link></li>
                                  <li><Link to="/outreach/requests"><span className="menu-item">Requests</span></Link></li>
                                </ul>
                              </li>
                              <li>
                                <Link
                                  to="#"
                                  onClick={(e) => { e.preventDefault(); setIsAccountabilityMenuOpen(!isAccountabilityMenuOpen); }}
                                  className="has-arrow waves-effect"
                                >
                                  <span className="nav-icon"><FontAwesomeIcon icon={faChartBar} /></span>
                                  <span className="menu-item">Accountability</span>
                                </Link>
                                <ul className={`sub-menu mm-${isAccountabilityMenuOpen ? "show" : "collapse"}`}>
                                  <li><Link to="/accountability/current"><span className="menu-item">Current</span></Link></li>
                                  <li><Link to="/accountability/history"><span className="menu-item">History</span></Link></li>
                                </ul>
                              </li>
                            </>
                          )}

                          {/* ── Settings ─────────────────────────── */}
                          <li>
                            <Link to="/callbacks/linkedin">
                              <span className="nav-icon"><FontAwesomeIcon icon={faShareNodes} /></span>
                              <span className="menu-item">Connections</span>
                            </Link>
                          </li>

                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="simplebar-placeholder"
                style={{ width: "254px", height: "1948px" }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
